import { Anchor, Div, Flex, Icon, P, Tag, Tooltip } from '@dnb/eufemia';
import { WarnIcon } from '@dnb/eufemia/components/FormStatus';
import {
  check as CheckIcon,
  close as CloseIcon,
  exclamation_circled as NotSharedIcon,
  globe as PublicIcon,
  office_buildings as OrgIcon,
  people_1 as TeamIcon,
} from '@dnb/eufemia/icons';
import { hasRequiredServiceNowInformation } from '@portals/shared/common/utils/apiUtils';
import { ProviderApiDto } from '@portals/shared/portal/ProviderApiDto';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import ResourceCard from '@/pages/profile/apps/components/ResourceCard';
import ApiStageBadge from '@/pages/profile/components/ApiStageBadge';
import ApiTypeBadge from '@/pages/profile/components/ApiTypeBadge';
import { apiOrEvent } from '@/utils/integrationServiceUtils';
import { isEmptyString } from '@/utils/textUtils';

import style from './index.module.css';

function missingServiceNowTooltip(type: 'REST' | 'EVENT', approvalStage: 'test' | 'live' | 'public') {
  if (approvalStage === 'public') return;
  const environment = approvalStage === 'test' ? 'test or live' : 'live';
  return (
    <Tooltip targetElement={<WarnIcon title="" />}>
      <P>Requesting access to this {apiOrEvent(type)} is enabled, but some ServiceNow information is missing.</P>
      <P>Consumer apps will not be able to request {environment} access before this information is provided.</P>
    </Tooltip>
  );
}

const publicTag = (
  <Tooltip
    // Tag must be wrapped in div, otherwise tooltip won't appear
    targetElement={
      <div>
        <Tag icon={<PublicIcon />} text="Public" />
      </div>
    }
  >
    Visible to everyone, including users who are not logged in.
  </Tooltip>
);

function OrganizationTag({ name }: { name: string }) {
  // Tag must be wrapped in div, otherwise tooltip won't appear
  const tag = (
    <div>
      <Tag icon={<OrgIcon />} text={name} />
    </div>
  );
  return <Tooltip targetElement={tag}>Visible to the users within this organization.</Tooltip>;
}

function TeamTag({ name }: { name: string }) {
  // Tag must be wrapped in div, otherwise tooltip won't appear
  const tag = (
    <div>
      <Tag icon={<TeamIcon />} text={name} />
    </div>
  );
  return <Tooltip targetElement={tag}>Visible to the users within this team.</Tooltip>;
}

const notSharedTag = (
  <Tooltip
    targetElement={
      // Tag must be wrapped in div, otherwise tooltip won't appear
      <div>
        <Tag icon={<NotSharedIcon />} text="None" />
      </div>
    }
  >
    Visibility has not been configured. Visibility will be limited to the users in the owner team.
  </Tooltip>
);

const enabledIcon = <Icon color="var(--color-success-green)" icon={CheckIcon} />;
const disabledIcon = <Icon color="var(--color-fire-red)" icon={CloseIcon} />;

interface Props {
  api: ProviderApiDto;
}

export default function ProviderApiCard({ api }: Props) {
  const hasDescription = !isEmptyString(api.description);

  return (
    <ResourceCard
      api={api}
      headerContent={
        <Flex.Container align="center" stretch>
          <Flex.Item grow>
            <Flex.Horizontal align="center" gap="x-small">
              <Anchor element={Link} to={`api/${api.id}`}>
                {api.name}
              </Anchor>
              <ApiStageBadge compact stage={api.stage} />
              {api.attachable &&
                !hasRequiredServiceNowInformation(api) &&
                missingServiceNowTooltip(api.type, api.approvalStage)}
            </Flex.Horizontal>
          </Flex.Item>

          <Flex.Item>
            <ApiTypeBadge type={api.type} />
          </Flex.Item>
        </Flex.Container>
      }
      key={api.id}
    >
      <Div className={style['ApiInformationGrid']}>
        <P weight="medium">Description:</P>
        <P className={classnames({ NoData: !hasDescription })}>{hasDescription ? api.description : 'No description'}</P>

        <P weight="medium">Classification:</P>
        <P>{api.classification}</P>

        <P weight="medium">Visible to:</P>
        <Flex.Horizontal gap="x-small">
          {api.isPublic ? (
            publicTag
          ) : api.organizations.length > 0 || api.teams.length > 0 ? (
            <>
              {api.organizations.map((orgName) => (
                <OrganizationTag key={orgName} name={orgName} />
              ))}
              {api.teams.map((teamName) => (
                <TeamTag key={teamName} name={teamName} />
              ))}
            </>
          ) : (
            notSharedTag
          )}
        </Flex.Horizontal>

        <P weight="medium">Allows access requests:</P>
        {api.attachable ? (
          <Flex.Horizontal gap="x-small">
            <Tag icon={enabledIcon} text="Test" />
            <Tag icon={api.availableInLiveMode ? enabledIcon : disabledIcon} text="Live" />
          </Flex.Horizontal>
        ) : (
          <P>Disabled</P>
        )}
      </Div>
    </ResourceCard>
  );
}
